import React, { useState } from "react";
import { adminPlanService } from "../../../services/api/settings";
import { ServiceType } from "../../../store/types/settings";
import { Plus, Trash2, CheckCircle2 } from "lucide-react";

const initialFormState = {
  title: "",
  subtitle: "",
  description: "",
  points: [] as string[],
  serviceType: "CONSOLE" as ServiceType,
  tier: "",
  monthlyPrice: 0,
  quarterlyPrice: 0,
  yearlyPrice: 0,
  setupFee: 0,
  integrationCost: 0,
  users: "",
  isHighlighted: false,
  syncPricing: true,
  deploymentCosts: [] as { name: string; description: string; price: number }[],
};

const CreatePlan: React.FC = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPoint, setNewPoint] = useState("");

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : type === "number"
            ? Number(value)
            : value,
    }));
  };

  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedType = e.target.value as ServiceType;
    setFormData({
      ...initialFormState,
      serviceType: selectedType,
      tier:
        selectedType === "CONSOLE"
          ? ""
          : selectedType === "DIRECT"
            ? "GROWTH"
            : "ENTERPRISE",
    });
    setNewPoint("");
  };

  const addPoint = () => {
    if (newPoint.trim()) {
      setFormData((prev) => ({
        ...prev,
        points: [...prev.points, newPoint.trim()],
      }));
      setNewPoint("");
    }
  };

  const removePoint = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      points: prev.points.filter((_, i) => i !== index),
    }));
  };

  const addDeploymentCost = () => {
    setFormData((prev) => ({
      ...prev,
      deploymentCosts: [
        ...prev.deploymentCosts,
        { name: "", description: "", price: 0 },
      ],
    }));
  };

  const updateDeploymentCost = (
    index: number,
    field: string,
    value: string | number,
  ) => {
    setFormData((prev) => {
      const newCosts = [...prev.deploymentCosts];
      newCosts[index] = { ...newCosts[index], [field]: value };
      return { ...prev, deploymentCosts: newCosts };
    });
  };

  const removeDeploymentCost = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      deploymentCosts: prev.deploymentCosts.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload: any = { ...formData };

      // Clean up tier if console
      if (formData.serviceType === "CONSOLE") {
        payload.tier = null;
      }

      // Cost Breakdown
      if (formData.deploymentCosts.length > 0) {
        payload.costBreakdown = {
          deploymentCosts: formData.deploymentCosts,
        };
      }
      delete payload.deploymentCosts;

      await adminPlanService.create(payload);

      setFormData({
        ...initialFormState,
        serviceType: formData.serviceType,
        tier:
          formData.serviceType === "CONSOLE"
            ? ""
            : formData.serviceType === "DIRECT"
              ? "GROWTH"
              : "ENTERPRISE",
      });
      setNewPoint("");
    } catch (error) {
      console.error("Error creating plan:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full py-5 font-abel">
      <div className="max-w-[800px] mx-auto px-3">
        <div className="flex border-b border-border text-20 pt-3 mb-6">
          <div className="border-b-[3px] border-secondary pb-1 cursor-pointer mr-8">
            <p className="font-[400]">Create Base Plan</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="border-b border-gray-200 pb-6 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Service Type
              </label>
              <select
                name="serviceType"
                value={formData.serviceType}
                onChange={handleServiceTypeChange}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                required
              >
                <option value="CONSOLE">Console (SaaS Platform)</option>
                <option value="RECRUITER">AI Recruiter</option>
                <option value="DIRECT">Direct (Modules)</option>
              </select>
            </div>

            <div className="space-y-4 animate-in fade-in duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan Title (e.g. Enterprise Suite)
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                    placeholder="Enter plan title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="For Large Orgs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  required
                  placeholder="Unlimited seats and custom workflows"
                />
              </div>

              {formData.serviceType !== "CONSOLE" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tier Assignment
                  </label>
                  <select
                    name="tier"
                    value={formData.tier}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    {formData.serviceType === "RECRUITER" ? (
                      <>
                        <option value="STARTER">STARTER</option>
                        <option value="GROWTH">GROWTH</option>
                        <option value="SCALE">SCALE</option>
                        <option value="ENTERPRISE">ENTERPRISE</option>
                      </>
                    ) : (
                      <>
                        <option value="STARTER">STARTER</option>
                        <option value="GROWTH">GROWTH</option>
                        <option value="SCALE">SCALE</option>
                        <option value="ENTERPRISE">ENTERPRISE</option>
                      </>
                    )}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monthly Price
                  </label>
                  <input
                    type="number"
                    name="monthlyPrice"
                    value={formData.monthlyPrice}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                    min={0}
                  />
                  <p className="text-[10px] text-gray-400 mt-1.5 font-bold">
                    ₦{formData.monthlyPrice.toLocaleString()} Equivalent
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quarterly Price
                  </label>
                  <input
                    type="number"
                    name="quarterlyPrice"
                    value={formData.quarterlyPrice}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                    min={0}
                  />
                  <p className="text-[10px] text-gray-400 mt-1.5 font-bold">
                    ₦{formData.quarterlyPrice.toLocaleString()} Equivalent
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Yearly Price
                  </label>
                  <input
                    type="number"
                    name="yearlyPrice"
                    value={formData.yearlyPrice}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                    min={0}
                  />
                  <p className="text-[10px] text-gray-400 mt-1.5 font-bold">
                    ₦{formData.yearlyPrice.toLocaleString()} Equivalent
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Setup Fee
                  </label>
                  <input
                    type="number"
                    name="setupFee"
                    value={formData.setupFee}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Integration Cost
                  </label>
                  <input
                    type="number"
                    name="integrationCost"
                    value={formData.integrationCost}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    min={0}
                  />
                </div>
              </div>

              {formData.serviceType === "CONSOLE" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Users Limit Label
                  </label>
                  <input
                    type="text"
                    name="users"
                    value={formData.users}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Unlimited users"
                  />
                </div>
              )}

              {/* Points Manager */}
              <div className="pt-4 pb-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Plan Points / Features
                  </label>
                  <span className="text-xs font-bold text-primary bg-primary/5 px-2 py-0.5 rounded-full">
                    {formData.points.length} Points
                  </span>
                </div>

                <div className="space-y-2">
                  {formData.points.map((point, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 group animate-in slide-in-from-left-2 duration-200"
                    >
                      <div className="flex-1 flex items-center gap-3 px-4 py-3 bg-gray-50 border border-transparent rounded-lg">
                        <CheckCircle2 size={16} className="text-emerald-500" />
                        <span className="flex-1 text-sm font-medium text-gray-700">
                          {point}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePoint(index)}
                        className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}

                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="text"
                      placeholder="Add a new point..."
                      value={newPoint}
                      onChange={(e) => setNewPoint(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addPoint();
                        }
                      }}
                      className="flex-1 px-4 py-3 border border-gray-200 rounded-lg text-sm focus:border-primary transition-all outline-none"
                    />
                    <button
                      type="button"
                      onClick={addPoint}
                      disabled={!newPoint.trim()}
                      className="px-4 py-3 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-all disabled:opacity-50"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Deployment Costs Manager */}
              <div className="pt-4 pb-2 border-t border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Deployment Costs Breakdown
                  </label>
                  <span className="text-xs font-bold text-primary bg-primary/5 px-2 py-0.5 rounded-full">
                    {formData.deploymentCosts.length} Items
                  </span>
                </div>

                <div className="space-y-4">
                  {formData.deploymentCosts.map((cost, index) => (
                    <div
                      key={index}
                      className="p-4 bg-gray-50 border border-gray-200 rounded-lg relative group"
                    >
                      <button
                        type="button"
                        onClick={() => removeDeploymentCost(index)}
                        className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100"
                        title="Remove Item"
                      >
                        <Trash2 size={16} />
                      </button>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Name
                          </label>
                          <input
                            type="text"
                            value={cost.name}
                            onChange={(e) =>
                              updateDeploymentCost(
                                index,
                                "name",
                                e.target.value,
                              )
                            }
                            placeholder="e.g. Server Setup"
                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Price
                          </label>
                          <input
                            type="number"
                            value={cost.price}
                            onChange={(e) =>
                              updateDeploymentCost(
                                index,
                                "price",
                                Number(e.target.value),
                              )
                            }
                            placeholder="0"
                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                            required
                            min={0}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Description
                        </label>
                        <textarea
                          value={cost.description}
                          onChange={(e) =>
                            updateDeploymentCost(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          placeholder="e.g. Initial server deployment and configuration"
                          rows={2}
                          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                          required
                        />
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addDeploymentCost}
                    className="w-full py-3 flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 hover:text-primary hover:border-primary/30 transition-all text-sm font-medium"
                  >
                    <Plus size={18} />
                    Add Deployment Cost Item
                  </button>
                </div>
              </div>

              <div className="flex gap-6 pt-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="isHighlighted"
                    checked={formData.isHighlighted}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <span className="text-gray-700 text-sm font-medium">
                    Highlight Plan (Popular Tag)
                  </span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="syncPricing"
                    checked={formData.syncPricing}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <span className="text-gray-700 text-sm font-medium">
                    Sync Pricing with UI
                  </span>
                </label>
              </div>
            </div>

            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full md:w-auto px-8 py-3 bg-secondary text-white font-medium rounded-lg hover:bg-secondary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary transition-colors disabled:opacity-50"
              >
                {isSubmitting ? "Creating Plan..." : "Create Base Plan"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreatePlan;
